import { InspectionStepId, SummaryViewStepStatus } from '@areapif/areapif-shared'
import Icon from '@mdi/react'
import { Box, Card, CardMedia, Chip, Grid2, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import React from 'react'
import { useTranslate } from 'react-admin'

import { getRemoteUrl } from '../../lib/supabase'
import type { GalleryEvidence } from '../../utils/evidenceUtils'
import { INSPECTION_STEP_ICON_PATHS } from '../atoms/inspectionStepIcons'
import { STEP_STATUS_STYLES } from '../atoms/stepStatusStyles'

interface InspectionStepGalleryProps {
  stepId: InspectionStepId
  stepStatus: SummaryViewStepStatus
  stepTitle: string
  evidence: GalleryEvidence[]
  onImageClick?: (imagePath: string) => void
  /** Rendered at the end of the step's header row, e.g. the live page's upload. */
  action?: React.ReactNode
  className?: string
}

/**
 * InspectionStepGallery - Backoffice component for displaying inspection steps
 * with always-visible photo galleries. Optimized for laptop form factor.
 */
export const InspectionStepGallery: React.FC<InspectionStepGalleryProps> = ({
  stepId,
  stepStatus,
  stepTitle,
  evidence,
  onImageClick,
  action,
  className,
}) => {
  const t = useTranslate()
  const photoEvidence = evidence.filter(e => e.type === 'photo')
  const sealEvidence = evidence.filter(e => e.type === 'seal')

  // The strip's palette for the strip's status: the gallery icon and the header
  // circle of the same step read the same.
  const style = STEP_STATUS_STYLES[stepStatus]
  const stepIcon = (
    <Box
      data-testid={`step-gallery-icon-${stepId}`}
      data-status={stepStatus}
      sx={{
        display: 'inline-flex',
        padding: '8px',
        borderRadius: '50%',
        backgroundColor: style.backgroundColor,
        border: `2px solid ${style.borderColor}`,
      }}
    >
      <Icon path={INSPECTION_STEP_ICON_PATHS[stepId]} size="24px" color={style.iconColor} />
    </Box>
  )

  return (
    <StyledStepContainer className={className} data-testid={`step-gallery-${stepId}`}>
      {/* Step Header with Icon and Title */}
      <StyledStepHeader>
        {stepIcon}
        <Box sx={{ ml: 2, flexGrow: 1 }}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
            {stepTitle}
          </Typography>
        </Box>
        {action}
      </StyledStepHeader>

      {/* Evidence Gallery - Always Visible */}
      {evidence.length > 0 ? (
        <StyledEvidenceContainer>
          {/* Photo Evidence Gallery */}
          {photoEvidence.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Grid2 container spacing={2}>
                {photoEvidence.map(photo => (
                  <Grid2 size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={photo.id}>
                    <StyledPhotoCard onClick={() => onImageClick?.(photo.id ? getRemoteUrl('inspections', photo.id) : '')}>
                      <CardMedia
                        component="img"
                        height={120}
                        image={photo.id ? getRemoteUrl('inspections', photo.id) : '/placeholder-image.jpg'}
                        alt={`Foto ${photo.category ? t(`resources.historical_inspections.enums.photo_category.${photo.category}`) : 'sin categoría'} - ${photo.id}`}
                        sx={{ objectFit: 'cover' }}
                      />
                      {photo.category && (
                        <StyledCategoryChip
                          label={t(`resources.historical_inspections.enums.photo_category.${photo.category}`)}
                          size="small"
                          variant="filled"
                        />
                      )}
                    </StyledPhotoCard>
                  </Grid2>
                ))}
              </Grid2>
            </Box>
          )}

          {/* Seal Evidence Gallery */}
          {sealEvidence.length > 0 && (
            <Box>
              <Grid2 container spacing={2}>
                {sealEvidence.map(seal => (
                  <Grid2 size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={seal.id}>
                    <StyledSealCard onClick={() => onImageClick?.(seal.id ? getRemoteUrl('inspections', seal.id) : '')}>
                      <CardMedia
                        component="img"
                        height={120}
                        image={seal.id ? getRemoteUrl('inspections', seal.id) : '/placeholder-image.jpg'}
                        alt={`Precinto ${seal.category ? t(`resources.historical_inspections.enums.seal_category.${seal.category}`) : 'sin categoría'} - ${seal.serialId ?? seal.id}`}
                        sx={{ objectFit: 'cover' }}
                      />
                      <StyledSealOverlay>
                        {seal.category && (
                          <Chip
                            label={t(`resources.historical_inspections.enums.seal_category.${seal.category}`)}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              fontSize: '0.7rem',
                              mb: 0.5,
                            }}
                          />
                        )}
                        {seal.serialId && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'white',
                              fontWeight: 600,
                              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                            }}
                          >
                            {seal.serialId}
                          </Typography>
                        )}
                      </StyledSealOverlay>
                    </StyledSealCard>
                  </Grid2>
                ))}
              </Grid2>
            </Box>
          )}
        </StyledEvidenceContainer>
      ) : (
        <StyledNoEvidenceContainer>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Sin evidencias para este paso
          </Typography>
        </StyledNoEvidenceContainer>
      )}
    </StyledStepContainer>
  )
}

// Styled Components
const StyledStepContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
}))

const StyledStepHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 16,
})

const StyledEvidenceContainer = styled(Box)({
  marginTop: 16,
})

const StyledPhotoCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[4],
  },
}))

const StyledSealCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[4],
  },
}))

const StyledCategoryChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  fontSize: '0.7rem',
}))

const StyledSealOverlay = styled(Box)({
  position: 'absolute',
  bottom: 8,
  left: 8,
  right: 8,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
})

const StyledNoEvidenceContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
}))

export default InspectionStepGallery
